import {
  AVAILABLE_TIMES,
  AVAILABLE_TIMES_HOURS,
  type TAvailableTimesHoursKey,
} from "Constants/counselorGridFilters";
import { MEETING_TYPE_KEYS } from "Constants/meetingTypes";
import { filterTimeSlots } from "Services/counselor-time-slot-picker-service";
import {
  configureGoogleMaps,
  importLibrary,
} from "Services/google-maps-loader";
import {
  addMinutes,
  differenceInMinutes,
  differenceInYears,
  format,
  startOfDay,
} from "date-fns";
import { sprintf } from "sprintf-js";
import type {
  TCoordinates,
  TCounselorLoadParams,
  TPlaceResult,
  TSchedule,
  TScheduleItem,
  TUser,
} from "./types/counselor-grid-service-types";

export function getUserReturningCustomer(user: TUser): string | null {
  const numberOfCustomers = Number(user.number_of_customers);
  const numberOfRecurringCustomers = Number(user.number_of_reccuring_customers);

  if (
    !(
      Number.isFinite(numberOfCustomers) &&
      Number.isFinite(numberOfRecurringCustomers)
    ) ||
    numberOfCustomers < 5 ||
    numberOfRecurringCustomers === 0
  ) {
    return null;
  }

  // Helper to get greatest common divisor
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(numberOfRecurringCustomers, numberOfCustomers);

  const reducedRecurring = numberOfRecurringCustomers / divisor;
  const reducedTotal = numberOfCustomers / divisor;
  const text = window.theme_vars.counselorGridTexts.returningCustomer;

  return sprintf(text, reducedRecurring, reducedTotal);
}

export function getUserBookedInTheLatest72Hours(user: TUser): string {
  if (!user.number_of_bookings_in_the_last_3_days) {
    return "";
  }

  const numberOfBookingsInTheLast3Days = Number(
    user.number_of_bookings_in_the_last_3_days
  );
  const text = window.theme_vars.counselorGridTexts.bookedInTheLast72Hours;

  return numberOfBookingsInTheLast3Days > 1
    ? sprintf(text, numberOfBookingsInTheLast3Days)
    : "";
}

export function getUserMeetingTimes(user: TUser): string[] {
  const meetingTypes: string[] = [
    MEETING_TYPE_KEYS.VIDEO,
    MEETING_TYPE_KEYS.PHONE,
  ];
  // Check if the user has meeting slots for all types of meetings
  if (
    user.schedule &&
    Object.keys(user.schedule).length > 0 &&
    user.schedule
      .filter(({ isActive }) => isActive)
      .map(({ schedule }) =>
        schedule.all.filter(
          (timeslot: TScheduleItem) => timeslot.inactive === false
        )
      )
      .reduce((a, b) => a.concat(b), []).length > 0
  ) {
    meetingTypes.unshift(MEETING_TYPE_KEYS.IN_PERSON);
  }

  return meetingTypes;
}

export function getUserYearsOfExperience(
  user: TUser,
  highestYearsOfExperience: number,
  setHighestYearsOfExperience: (years: number) => void
): number {
  const activeSince = user.professionally_active_since;

  if (!activeSince) {
    return 0;
  }

  const yearsOfExperience = differenceInYears(new Date(), activeSince);

  if (yearsOfExperience > highestYearsOfExperience) {
    setHighestYearsOfExperience(yearsOfExperience);
  }

  return yearsOfExperience;
}

export function getUserAvailableTimes(user: TUser): Record<string, any> {
  if (!(user.schedule && Array.isArray(user.schedule))) {
    return {};
  }

  let availableTimes: Record<string, string[]> = {};
  const schedules = user.schedule.filter(({ isActive }) => isActive);
  const schedulesWithSlots = schedules
    .map((schedule) => {
      const { date, dayOfWeek, isActive, schedule: scheduleItems } = schedule;
      const { all, phone } = scheduleItems;
      const slots = all.concat(phone);

      return { date, dayOfWeek, isActive, schedule: slots };
    })
    .reduce((scheduleItems: TSchedule[], item) => {
      const filteredSchedule = filterTimeSlots(scheduleItems, item, 14);
      return filteredSchedule;
    }, []);

  // Check each schedule entry for overlaps with our defined time ranges
  schedulesWithSlots.forEach((scheduleDay) => {
    if (!scheduleDay.schedule || hasAllAvailableTimes(availableTimes)) {
      return;
    }

    availableTimes = categorizeAvailableTimeSlots(
      scheduleDay.schedule,
      scheduleDay.date,
      availableTimes
    );
  });

  return availableTimes;
}

function categorizeAvailableTimeSlots(
  schedule: TScheduleItem[],
  date: string,
  availableTimes: Record<string, string[]>
): Record<string, string[]> {
  const meetingLength = 45;

  for (const slot of schedule) {
    if (hasAllAvailableTimes(availableTimes)) {
      continue;
    }

    const [startHours, startMinutes] = slot.startTime.split(":").map(Number);
    const [endHours, endMinutes] = slot.endTime.split(":").map(Number);

    const start = startOfDay(new Date(date));
    const end = startOfDay(new Date(date));
    start.setHours(startHours, startMinutes);
    end.setHours(endHours, endMinutes);

    while (
      differenceInMinutes(end, start) >= meetingLength &&
      !hasAllAvailableTimes(availableTimes)
    ) {
      const endOfMeeting = addMinutes(start, meetingLength);
      const meetingTime = {
        available: slot.available,
        endTime: format(endOfMeeting, "HH:mm"),
        startTime: format(start, "HH:mm"),
      };

      for (const key of Object.keys(AVAILABLE_TIMES_HOURS)) {
        if (availableTimes[key]) {
          continue;
        }

        const isInRange = isTimeSlotWithinRange(
          AVAILABLE_TIMES_HOURS[key as TAvailableTimesHoursKey],
          meetingTime
        );

        if (isInRange) {
          if (availableTimes[key]) {
            const existingDate = new Date(availableTimes[key][0].date);
            const currentDate = new Date(date);
            if (currentDate < existingDate) {
              availableTimes[key] = {
                date,
                slots: [localeFormattedMeetingTime(meetingTime)],
              };
            }
          } else {
            availableTimes[key] = {
              date,
              slots: [localeFormattedMeetingTime(meetingTime)],
            };
          }
        }
      }

      start.setTime(endOfMeeting.getTime());
    }
  }

  return availableTimes;
}

function localeFormattedMeetingTime(meetingTime: TScheduleItem): TScheduleItem {
  const [startHours, startMinutes] = meetingTime.startTime
    .split(":")
    .map(Number);
  const [endHours, endMinutes] = meetingTime.endTime.split(":").map(Number);

  const start = new Date();
  const end = new Date();
  start.setHours(startHours, startMinutes);
  end.setHours(endHours, endMinutes);

  return {
    ...meetingTime,
    endTime: end.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    startTime: start.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

function isTimeSlotWithinRange(
  availableTimeRange: { startTime: string; endTime: string },
  userTimeRange: { startTime: string; endTime: string }
): boolean {
  return (
    availableTimeRange.startTime <= userTimeRange.startTime &&
    availableTimeRange.endTime >= userTimeRange.endTime
  );
}

function hasAllAvailableTimes(
  availableTimes: Record<string, string[]>
): boolean {
  return (
    availableTimes[AVAILABLE_TIMES.MORNING] &&
    availableTimes[AVAILABLE_TIMES.AFTERNOON] &&
    availableTimes[AVAILABLE_TIMES.EVENING]
  );
}

export function prepareCounselorLoadParams(
  cityTerm: { slug: string } | null,
  city: string,
  role: string,
  competence: string,
  adminMarket: string,
  siteId: string
): TCounselorLoadParams {
  const tzOffset = new Date().getTimezoneOffset() * 60_000;
  const localISOTime = new Date(Date.now() - tzOffset)
    .toISOString()
    .slice(0, -1);
  const tomorrow = localISOTime.slice(0, 10);
  const apiCity = cityTerm ? cityTerm.slug : city;

  return {
    adminMarket,
    city: apiCity,
    competence,
    includeReviews: true,
    limit: 0,
    role,
    siteId,
    skipLocationFiltering: true,
    startDate: tomorrow,
  };
}

export async function getUserDistance(
  user: TUser,
  location: TPlaceResult | undefined,
  googleMapsIsAvailable: boolean
): Promise<number> {
  if (!(googleMapsIsAvailable && user.address && location)) {
    return 0;
  }

  const locationCoordinates = {
    lat: location.location?.lat(),
    lng: location.location?.lng(),
  };

  const userCoordinates = {
    lat: user.address.coordinates.latitude,
    lng: user.address.coordinates.longitude,
  };

  return await calculateDistance(
    locationCoordinates,
    userCoordinates,
    googleMapsIsAvailable
  );
}

async function calculateDistance(
  origin: TCoordinates,
  destination: TCoordinates,
  googleMapsIsAvailable: boolean
) {
  if (!googleMapsIsAvailable) {
    return 0;
  }

  try {
    configureGoogleMaps();

    const { spherical } = await importLibrary("geometry");
    const { LatLng } = await importLibrary("core");

    // A failed library load must not throw — the grid filter would
    // silently drop the counselor instead of keeping the same
    // "couldn't compute" semantics as an unavailable Maps API.
    if (!(spherical && LatLng)) {
      return 0;
    }

    return spherical.computeDistanceBetween(
      new LatLng(origin.lat, origin.lng),
      new LatLng(destination.lat, destination.lng)
    );
  } catch (error) {
    console.error("Error calculating distance:", error);

    return 0;
  }
}
